/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomFilterV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { Box, Button, Divider, Popover, Typography } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CustomButtonV2 from "../button/CustomButtonV2";

function CustomFilterV2({
  iconicFilter = false,
  border = true,
  open,
  sections,
  widthPerColumn,
  handleClick,
  handleClose,
  showClearAll = false,
  anchorEl,
  id,
  showButton = true,
  handleFilterButton,
  actionButtonStyle = {},
}) {
  return (
    <Box>
      {/* Filter Button */}
      <Button
        aria-describedby={id}
        variant={border ? "outlined" : "text"}
        endIcon={!iconicFilter && <FilterAltOutlinedIcon />}
        onClick={handleClick}
        sx={{
          textTransform: "none",
          borderColor: "#000",
          color: "#000",
          borderRadius: 0,
          "&:hover": {
            borderColor: "#000",
            backgroundColor: "#f5f5f5",
            borderRadius: 0,
          },
        }}
      >
        {iconicFilter ? (
          <FilterAltOutlinedIcon />
        ) : (
          <Typography>Filter</Typography>
        )}
      </Button>

      {/* Popover */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{ mt: 1 }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxHeight: "80vh",
            borderRadius: 0,
          }}
        >
          {/* 🔹 Columns with vertical dividers between sections */}
          <Box
            sx={{
              display: "flex",
              overflowX: "auto",
              gap: 2,
              pb: 1,
              pr: 1,
              borderRadius: 0,
            }}
          >
            {sections.map((section, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                }}
              >
                {/* Section Box */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,

                    ...(widthPerColumn
                      ? { width: widthPerColumn }
                      : { minWidth: "20rem" }),
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 500,
                        color: "#63666A",
                      }}
                    >
                      {section.heading}
                    </Typography>
                    <Divider sx={{ mt: 0.5 }} />
                  </Box>

                  <Box
                    sx={{
                      flexGrow: 1,
                      minHeight: "220px",
                    }}
                  >
                    {section.content}
                  </Box>
                </Box>

                {/* Divider (not after the last column) */}
                {idx !== sections.length - 1 && (
                  <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                )}
              </Box>
            ))}
          </Box>

          {/* 🔹 Footer aligned to end */}
          {showButton ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                pt: 1,
                borderTop: "1px solid #e0e0e0",

                ...actionButtonStyle,
              }}
            >
              {[
                ...(showClearAll
                  ? [
                    {
                      type: "cancel", // neutral type
                      btnName: "Clear All",
                      action: "discard",
                    },
                  ]
                  : []),
                {
                  type: "cancel",
                  btnName: "Cancel",
                  action: "cancel",
                },
                {
                  type: "submit",
                  btnName: "Apply",
                  action: "apply",
                },
              ].map(({ type, btnName, action }, idx) => (
                <CustomButtonV2
                  key={idx}
                  type={type}
                  btnName={btnName}
                  handleOnClick={(e) => handleFilterButton(action)}
                />
              ))}
            </Box>
          ) : null}
        </Box>
      </Popover>
    </Box>
  );
}

export default CustomFilterV2;
